import _ from "lodash";
import { CourseDTO, CourseFlatDTO } from "../domain/models/dto/CourseDTO";
import {
    ICourse,
    CourseFiltersValues,
    CourseCollection,
} from "../domain/models/Course";
import { CourseService } from "../domain/services/CourseService";
import { CourseMapper } from "../mappers/CourseMapper";
import { CollectionResponse, RemoveResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";

export class CourseServiceImpl
    extends BaseServiceImpl
    implements CourseService
{
    static _instance: CourseService;
    private courseMapper = new CourseMapper();
    private readonly courseEndpoint = "/api/v1/courses";

    private constructor() {
        super();
    }

    static getInstance(): CourseService {
        if (!CourseServiceImpl._instance) {
            CourseServiceImpl._instance = new CourseServiceImpl();
        }
        return CourseServiceImpl._instance;
    }

    instantiate(entity: ICourse | CourseDTO): ICourse {
        return this.courseMapper.fromDTO(entity as CourseDTO);
    }

    async getAll(
        currentPage: number,
        currentLimit: number,
        filtersDTO: CourseFiltersValues = {} as CourseFiltersValues,
    ): Promise<CollectionResponse<ICourse>> {
        try {
            let url = `${this.courseEndpoint}?limit=${currentLimit}&page=${currentPage}&getEntityTranslations=true`;

            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (key && !_.isNil(value) && !_.isEmpty(value)) {
                    url += `&${key}=${value}`;
                }
            });

            const result = await this.get<CollectionResponse<CourseDTO>>(url);

            if (!result) {
                console.error("API returned undefined");
                return {
                    rows: [],
                    totalRows: 0,
                    totalPages: 0,
                    currentPage: currentPage,
                    limit: currentLimit,
                };
            }

            return {
                ...result,
                rows: (result.rows || []).map((dto: CourseDTO) => {
                    const model = this.courseMapper.fromDTO(dto);
                    return model;
                }),
            };
        } catch (err) {
            console.error("Error in CourseServiceImpl.getAll");
            throw new Error(String(err));
        }
    }

    async createOrUpdate(dto: ICourse): Promise<ICourse> {
        try {
            const encodedDto = this.courseMapper.toDTO(dto);
            if (dto.id) {
                return this.patch(
                    `${this.courseEndpoint}/${dto.id}`,
                    encodedDto,
                );
            }

            return this.post(this.courseEndpoint, encodedDto);
        } catch (err) {
            console.error("Error in CourseServiceImpl.createOrUpdate");
            throw new Error(String(err));
        }
    }

    async deleteById(id: string): Promise<RemoveResponse> {
        try {
            if (!id) return { status: false };
            return super.delete(`${this.courseEndpoint}/${id}`);
        } catch (err) {
            console.error("Error in CourseServiceImpl.deleteById");
            throw new Error(String(err));
        }
    }

    async getById(id: string): Promise<ICourse | null> {
        try {
            if (!id) return null;

            const dto = await this.get<CourseDTO>(
                `${this.courseEndpoint}/${id}?getEntityTranslations=true`,
            );
            const model = this.courseMapper.fromDTO(dto);
            return model;
        } catch (err) {
            console.error("Error in CourseServiceImpl.getById");
            return null;
        }
    }
}

export function courseServiceFactory(): CourseService {
    return CourseServiceImpl.getInstance();
}
